// Staff-side portal access for one matter: who can sign in, with what, and
// the invitation that hands the client an account. Admin-only on the
// server (mounted behind requireAdmin), so the card that uses it is too.

import { getAuthHeaders } from "./auth";

export interface ClientAccessRow {
  accessEnabled: boolean;
  clientEId: string;
  /** Set when an invitation goes out - it is the address that signs in. */
  email: string | null;
  /** Set when the client spends the link. Invited is not signed up. */
  passwordSetAt: string | null;
  lastLoginAt: string | null;
  loginCount: number;
  invitationSentAt: string | null;
}

export interface ClientAccessStatus {
  access: ClientAccessRow | null;
  /** Where an invitation would go, from the client's file. */
  inviteEmail: string | null;
}

function base(caseId: string): string {
  return `/api/admin/cases/${caseId}/client-access`;
}

async function readError(res: Response, fallback: string): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return body.error ?? fallback;
}

export async function fetchClientAccess(caseId: string): Promise<ClientAccessStatus> {
  const res = await fetch(base(caseId), {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(await readError(res, `GET client access failed: ${res.status}`));
  return (await res.json()) as ClientAccessStatus;
}

/** Turns access on, minting the case eId if it has none. The date of birth is the old credential. */
export async function enableClientAccess(caseId: string, clientDob: string): Promise<void> {
  const res = await fetch(base(caseId), {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    credentials: "same-origin",
    body: JSON.stringify({ clientDob }),
  });
  if (!res.ok) throw new Error(await readError(res, "Could not turn access on."));
}

export async function setClientAccessEnabled(caseId: string, enabled: boolean): Promise<void> {
  const res = await fetch(base(caseId), {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    credentials: "same-origin",
    body: JSON.stringify({ accessEnabled: enabled }),
  });
  if (!res.ok) throw new Error(await readError(res, "Could not change access."));
}

/** Mails the one-time link that lets the client choose a password.
    queued means the mailer is retrying it - sent, but not yet delivered. */
export async function inviteClient(caseId: string): Promise<{ to: string; queued: boolean }> {
  const res = await fetch(`${base(caseId)}/invite`, {
    method: "POST",
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  const body = (await res.json().catch(() => ({}))) as {
    to?: string;
    queued?: boolean;
    error?: string;
  };
  // The refusals carry the reason - no email on file, one went out
  // minutes ago, mail is not connected - and each needs a different move
  // from the office, so none of them may be flattened into one message.
  if (!res.ok) throw new Error(body.error ?? "Could not send the invitation.");
  return { to: body.to ?? "the client", queued: body.queued === true };
}
